import { Box, Paper, SvgIcon, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../../../../atomic';
import FlagIcon from '../../../../../icons/svg/info.svg?react';

type Props = {
  profilePictureUrl?: string;
  employeeFirstName: string;
  employeeLastName: string;
  employeePosition?: string;
  employeeStartDate?: string;
  onAvatarClicked?: () => void;
};

export const UserInfoBox: FC<Props> = ({
  profilePictureUrl,
  employeeFirstName,
  employeeLastName,
  employeePosition,
  employeeStartDate,
  onAvatarClicked
}) => {
  const [height, setHeight] = useState(24);
  const ref = useRef(null);
  const { t } = useTranslation('template');

  const initials = employeeFirstName.substring(0, 1) + employeeLastName.substring(0, 1);

  useEffect(() => {
    // @ts-expect-error unable to fix ts error
    const size = ref.current?.clientHeight - 32; //calculate height of the parent container
    setHeight(size < 36 ? 36 : size);
  }, [ref.current, profilePictureUrl, initials]);

  return (
    <Paper
      ref={ref}
      sx={{
        padding: '1rem',
        display: 'grid',
        gridTemplateColumns: 'fit-content(24px) 1fr',
        gridColumnGap: '1rem'
      }}
    >
      <Avatar
        sx={{
          gridRow: '1 / span 3',
          borderRadius: '12px',
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundColor: 'white',
          '&:hover': { cursor: onAvatarClicked ? 'pointer' : 'unset' }
        }}
        src={`${profilePictureUrl}`}
        initials={initials}
        sizes={`${height}px`}
        onClick={onAvatarClicked}
        imgProps={{
          style: { objectFit: 'contain' }
        }}
      />
      <Typography variant="h3" lineHeight="20px">
        {`${employeeFirstName} ${employeeLastName}`}
      </Typography>
      {employeePosition && <Typography variant="body2">{employeePosition}</Typography>}
      {employeeStartDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: '6px' }}>
          <SvgIcon
            component={FlagIcon}
            sx={{ marginRight: '4px', color: 'grey.900', fontSize: '12px' }}
          />
          <Typography variant="caption" color="grey.900">
            {formatStartDate(t, employeeStartDate)}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const formatStartDate = (t: TFunction, date: string): string => {
  const startDate = new Date(date);
  const today = new Date();
  const diff: number =
    Math.round((today.getTime() - startDate.getTime()) / (365 * 24 * 60 * 60 * 100)) / 10;

  return `${startDate.toLocaleDateString(undefined, {
    dateStyle: 'medium'
  })} (${diff.toLocaleString()} ${t('YEARS', { count: diff })})`;
};
